import { useMemo } from "react";
import en from "@/i18n/mobile-signing-locales/en.json";
import ja from "@/i18n/mobile-signing-locales/ja.json";
import ko from "@/i18n/mobile-signing-locales/ko.json";
import ar from "@/i18n/mobile-signing-locales/ar.json";
import ptBr from "@/i18n/mobile-signing-locales/pt-br.json";
import es from "@/i18n/mobile-signing-locales/es.json";
import de from "@/i18n/mobile-signing-locales/de.json";
import fr from "@/i18n/mobile-signing-locales/fr.json";
import zhHk from "@/i18n/mobile-signing-locales/zh-hk.json";
import hi from "@/i18n/mobile-signing-locales/hi.json";

export interface MobileSigningLocaleCatalog {
  locale: string;
  dir: "ltr" | "rtl";
  pairing: {
    title: string;
    description: string;
    button: string;
    buttonBusy: string;
    machineBlocked: string;
    codeLabel: string;
    qrHint: string;
    expiresLabel: string;
    expiredLabel: string;
    expiredHint: string;
    regenerate: string;
    copy: string;
    copied: string;
    stepUpFailed: string;
    genericError: string;
    unavailable: string;
    getAndroidApp: string;
  };
  /** D714 self-service pairing ("Pair my phone" on /signer/get-started). */
  selfPairing: {
    title: string;
    description: string;
    button: string;
    alreadyBound: string;
    disabled: string;
    roleRequired: string;
  };
  invites: {
    signerRoleExplanation: string;
    deliveryStatus: {
      queued: string;
      sent: string;
      failed: string;
      notConfigured: string;
      delivered: string;
      bounced: string;
    };
    deliveryShareHint: string;
  };
  transferState: {
    pendingDeviceSignature: string;
    pendingDeviceSignatureDetail: string;
    broadcast: string;
    confirmed: string;
  };
  wallet: {
    trioChip: string;
  };
  signerStart: {
    title: string;
    intro: string;
    step1Title: string;
    step1Body: string;
    step1Action: string;
    step2Title: string;
    step2Body: string;
    step2EmailLabel: string;
    step3Title: string;
    step3Body: string;
  };
  getApp: {
    title: string;
    intro: string;
    androidTitle: string;
    checking: string;
    signInFirst: string;
    signIn: string;
    download: string;
    sha256Label: string;
    versionLabel: string;
    unavailable: string;
    unavailableTransient: string;
    tryAgain: string;
    installTitle: string;
    installAllow: string;
    installSamsung: string;
    installThenPair: string;
    iosTitle: string;
    iosNotAvailable: string;
    breadcrumbHome: string;
    back: string;
    pairNextTitle: string;
    pairNextBody: string;
    pairNextCta: string;
  };
}

/**
 * Locale catalog for the mobile-signing surfaces (pair-a-device, the
 * pending-device-signature / broadcast / confirmed transfer states, and the
 * Trio wallet chip), plus the admin-invite surfaces that describe signer
 * onboarding (the transaction_signer role explanation and invite delivery
 * status labels). Same 10-locale set and the same lightweight,
 * dependency-free key -> catalog approach as src/i18n/use-error-locale.ts
 * (ALL-4857 STEP 0: no i18n library is a dependency of this app), kept as
 * its own catalog rather than growing the unrelated error-page one.
 */
export const MOBILE_SIGNING_LOCALES: Record<string, MobileSigningLocaleCatalog> = {
  en: en as MobileSigningLocaleCatalog,
  ja: ja as MobileSigningLocaleCatalog,
  ko: ko as MobileSigningLocaleCatalog,
  ar: ar as MobileSigningLocaleCatalog,
  "pt-br": ptBr as MobileSigningLocaleCatalog,
  es: es as MobileSigningLocaleCatalog,
  de: de as MobileSigningLocaleCatalog,
  fr: fr as MobileSigningLocaleCatalog,
  "zh-hk": zhHk as MobileSigningLocaleCatalog,
  hi: hi as MobileSigningLocaleCatalog,
};

export const DEFAULT_MOBILE_SIGNING_LOCALE = "en";

/** Same resolution order as resolveErrorLocale: explicit `?locale=` query
 * param, then the browser's language, then English. */
export function resolveMobileSigningLocale(searchParams?: URLSearchParams): string {
  const requested = searchParams?.get("locale");
  if (requested && MOBILE_SIGNING_LOCALES[requested]) return requested;

  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase();
    if (nav && MOBILE_SIGNING_LOCALES[nav]) return nav;
    const short = nav?.split("-")[0];
    if (short && MOBILE_SIGNING_LOCALES[short]) return short;
  }
  return DEFAULT_MOBILE_SIGNING_LOCALE;
}

export function useMobileSigningLocale(searchParams?: URLSearchParams): MobileSigningLocaleCatalog {
  const search = searchParams?.toString() ?? "";
  return useMemo(() => {
    const code = resolveMobileSigningLocale(searchParams);
    return MOBILE_SIGNING_LOCALES[code] ?? MOBILE_SIGNING_LOCALES[DEFAULT_MOBILE_SIGNING_LOCALE];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
}
